import { createContext, useMemo, type ReactNode } from "react";

import {
  createEnumParam,
  StringParam,
  useQueryParam,
  withDefault,
} from "use-query-params";

import {
  MCAttributionWindowDays,
  NBAccountingMode,
  type IMCAttributionWindowDays,
  type INBAccountingMode,
} from "@north-beam/nb-common";

const AccountingModeParam = createEnumParam<INBAccountingMode>(
  NBAccountingMode.keyOrder,
);

const AttributionWindowParam = createEnumParam<IMCAttributionWindowDays>(
  MCAttributionWindowDays.keyOrder,
);

interface UserAttributionSettingsContextDescriptor {
  attributionModel: string;
  attributionWindow: IMCAttributionWindowDays;
  accountingMode: INBAccountingMode;
  setAttributionModel: (value: string) => void;
  setAccountingMode: (value: INBAccountingMode) => void;
  setAttributionWindow: (value: IMCAttributionWindowDays) => void;
}

export const UserAttributionSettingsContext =
  createContext<UserAttributionSettingsContextDescriptor | null>(null);

export const UserAttributionSettingProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const [attributionModel, setAttributionModel] = useQueryParam(
    "attributionModel",
    withDefault(StringParam, "northbeam_custom"),
    { updateType: "pushIn" },
  );
  const [attributionWindow, setAttributionWindow] = useQueryParam(
    "attributionWindow",
    withDefault(AttributionWindowParam, "1" as const),
    { updateType: "pushIn" },
  );
  const [accountingMode, setAccountingMode] = useQueryParam(
    "accountingMode",
    withDefault(AccountingModeParam, "accrual" as const),
    { updateType: "pushIn" },
  );

  const normalizedAttributionWindow = useMemo(() => {
    if (accountingMode === "cash") {
      return "infinity";
    }

    if (accountingMode === "accrual" && attributionWindow === "infinity") {
      return "1";
    }

    return attributionWindow;
  }, [accountingMode, attributionWindow]);

  return (
    <UserAttributionSettingsContext.Provider
      value={{
        attributionModel,
        attributionWindow: normalizedAttributionWindow,
        accountingMode,
        setAttributionModel,
        setAttributionWindow,
        setAccountingMode,
      }}
    >
      {children}
    </UserAttributionSettingsContext.Provider>
  );
};
